import React from 'react'
import type { ProjectDetailStats } from '../../../types/projectAnalysis'
import { MONTH_NAMES } from '../../../utils/projectAnalysisPeriod'
import { ExportMenuButton } from '../../common/export-menu-button'
import {
  exportResourcesCsv,
  exportResourcesExcel,
  getResourcesExportRows,
  safeFilename,
} from '../../../utils/projectAnalysisExport'
import { utilBadgeClass } from './project-detail-util-badge'

export interface ProjectDetailResourcesTabProps {
  stats: ProjectDetailStats
  projectId: number
  projectDetailName: string
  expandedResourceId: number | null
  onToggleResource: (userId: number) => void
}

export function ProjectDetailResourcesTab({
  stats: s,
  projectId,
  projectDetailName,
  expandedResourceId,
  onToggleResource,
}: ProjectDetailResourcesTabProps) {
  const nameSafe = safeFilename(projectDetailName)

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-md overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900 min-w-0">
          Resource-wise hours ({s.usersData.length}) — click row for monthly
          breakdown
        </h3>
        <div className="flex justify-end w-full sm:w-auto shrink-0">
          <ExportMenuButton
            disabled={!s.usersData.length}
            onCsv={() => {
              const rows = getResourcesExportRows(s, projectId)
              exportResourcesCsv(rows, nameSafe)
            }}
            onExcel={() => {
              const rows = getResourcesExportRows(s, projectId)
              exportResourcesExcel(rows, nameSafe)
            }}
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="w-8 px-2 py-2" />
              <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase text-slate-500">
                Resource
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                Total
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                Billable
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                Non-bill.
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                Util. %
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                Late %
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase text-slate-500">
                Leave
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {s.usersData.map((u) => {
              const util =
                u.total_hours > 0 ? (u.billable_hours / u.total_hours) * 100 : 0
              const entries = Number(u.entries_count) || 0
              const late = Number(u.late_entries_count) || 0
              const latePct = entries > 0 ? (late / entries) * 100 : null
              const leaveDays = Math.round((u.leave_days ?? 0) * 10) / 10
              const isExpanded = expandedResourceId === u.user_id
              const monthlyRows = s.userMonthlyBreakdown?.get(u.user_id) || []
              return (
                <React.Fragment key={u.user_id}>
                  <tr
                    onClick={() => onToggleResource(u.user_id)}
                    className={[
                      'cursor-pointer',
                      isExpanded ? 'bg-slate-100' : '',
                      'hover:bg-slate-50',
                    ].join(' ')}
                  >
                    <td className="px-2 py-2 text-center">
                      <span
                        className="inline-block transition-transform"
                        style={{
                          transform: isExpanded ? 'rotate(90deg)' : 'none',
                        }}
                      >
                        ▸
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs font-medium text-slate-900">
                      {u.full_name || u.username || `User ${u.user_id}`}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                      {u.total_hours.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-emerald-700 font-medium tabular-nums">
                      {u.billable_hours.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                      {u.non_billable_hours.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${utilBadgeClass(util)}`}
                      >
                        {util.toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-600">
                      {latePct != null ? `${latePct.toFixed(1)}%` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-xs text-slate-600 tabular-nums">
                      {leaveDays.toLocaleString(undefined, {
                        maximumFractionDigits: 1,
                      })}
                    </td>
                  </tr>
                  {isExpanded && monthlyRows.length > 0 && (
                    <tr className="bg-slate-50/80">
                      <td colSpan={8} className="px-4 py-3">
                        <div className="rounded-lg border border-slate-200 overflow-hidden">
                          <div className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100">
                            Monthly breakdown —{' '}
                            {u.full_name || u.username || `User ${u.user_id}`}
                          </div>
                          <table className="min-w-full divide-y divide-slate-200">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="px-3 py-1.5 text-left text-[10px] font-medium uppercase text-slate-500">
                                  Period
                                </th>
                                <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                  Total
                                </th>
                                <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                  Billable
                                </th>
                                <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                  Non-bill.
                                </th>
                                <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                  Util.
                                </th>
                                <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                  Late %
                                </th>
                                <th className="px-3 py-1.5 text-right text-[10px] font-medium uppercase text-slate-500">
                                  Leave
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                              {monthlyRows.map((row) => {
                                const rowEntries = Number(row.entries_count) || 0
                                const rowLate = Number(row.late_entries_count) || 0
                                const rowLatePct =
                                  rowEntries > 0 ? (rowLate / rowEntries) * 100 : null
                                return (
                                  <tr key={row.name}>
                                    <td className="px-3 py-1.5 text-[11px] font-medium text-slate-700">
                                      {MONTH_NAMES[row.month - 1]} {row.year}
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-[11px] text-slate-600 tabular-nums">
                                      {row.total_hours.toLocaleString(undefined, {
                                        maximumFractionDigits: 2,
                                      })}
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-[11px] text-emerald-700 tabular-nums">
                                      {row.billable_hours.toLocaleString(undefined, {
                                        maximumFractionDigits: 2,
                                      })}
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-[11px] text-slate-600 tabular-nums">
                                      {row.non_billable_hours.toLocaleString(undefined, {
                                        maximumFractionDigits: 2,
                                      })}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                      <span
                                        className={`px-1 py-0.5 rounded text-[10px] font-medium ${utilBadgeClass(row.util)}`}
                                      >
                                        {row.util.toFixed(0)}%
                                      </span>
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-[11px] text-slate-600">
                                      {rowLatePct != null
                                        ? `${rowLatePct.toFixed(1)}%`
                                        : '—'}
                                    </td>
                                    <td className="px-3 py-1.5 text-right text-[11px] text-slate-600 tabular-nums">
                                      {(row.leave_days ?? 0).toLocaleString(undefined, {
                                        maximumFractionDigits: 1,
                                      })}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
